/**
 * The chapter/chunk index over a loaded document: which chunk plays next, which
 * one a stale selection should fall back to, and how a legacy chapter or TTS id
 * bridges to a doc-pack reading anchor.
 *
 * Pure, and total — a selection that does not describe this document returns
 * `undefined` rather than throwing, because the callers that hit it (a
 * rechunked conversion mid-playback) have to tell that apart from reaching the
 * end of the document.
 */
import type { AudioItemDetail } from '@listening/api-contract/legacy-client-types';

export const docPackAnchorForLegacyChapterStart = (
  documentSummary: Pick<AudioItemDetail, 'chapters'>,
  chapterId: string | undefined,
) => {
  if (!chapterId) return;
  const chapter = documentSummary.chapters.find((c) => c.id === chapterId);
  const page = chapter?.bbox_pages[0];
  if (!page) return;
  return { page, charStart: 0 };
};

export const docPackAnchorForLegacyTtsChunk = (
  documentSummary: Pick<AudioItemDetail, 'chapters'>,
  ttsId: string | undefined,
) => {
  if (!ttsId) return;
  const chapter = documentSummary.chapters.find((c) =>
    c.tts_ids.includes(ttsId),
  );
  return docPackAnchorForLegacyChapterStart(documentSummary, chapter?.id);
};

/** The chapter to play for a doc-pack page when the load plays chapters — the
 * reverse of `docPackAnchorForLegacyChapterStart`, at the same chapter
 * granularity. A page shared by two chapters picks the earlier, so playback
 * starts at or before the position; a page no chapter covers (content the
 * chapters never narrated) picks the last chapter that starts before it. */
export const legacyChapterForDocPackPage = (
  documentSummary: Pick<AudioItemDetail, 'chapters'>,
  page: number,
) =>
  (
    documentSummary.chapters.find((chapter) =>
      chapter.bbox_pages.includes(page),
    ) ??
    documentSummary.chapters.findLast(
      (chapter) =>
        chapter.bbox_pages[0] !== undefined && chapter.bbox_pages[0] <= page,
    )
  )?.id;

/** The first playable chunk at or after `chapterId`. `undefined` when nothing
 * later in the document has audio, and when `chapterId` does not name a chapter
 * of this document — a rechunked conversion, which the caller distinguishes
 * with `isLegacyAudioChunkSelectionStale`. */
export function findFirstAudioChunkAtOrAfterChapter(
  documentSummary: Pick<AudioItemDetail, 'chapters'>,
  chapterId: string,
) {
  const startChapterIdx = documentSummary.chapters.findIndex(
    (chapter) => chapter.id === chapterId,
  );
  if (startChapterIdx === -1) return;
  for (const chapter of documentSummary.chapters.slice(startChapterIdx)) {
    const ttsId = chapter.tts_ids[0];
    if (ttsId) return { chapterId: chapter.id, ttsId };
  }
}

export function resolveLegacyAudioChunkSelection(
  documentSummary: Pick<AudioItemDetail, 'chapters'>,
  chapterId: string | undefined,
  ttsId: string | undefined,
) {
  const chapter = chapterId
    ? documentSummary.chapters.find((c) => c.id === chapterId)
    : ttsId
      ? documentSummary.chapters.find((c) => c.tts_ids.includes(ttsId))
      : undefined;
  if (ttsId && chapter?.tts_ids.includes(ttsId)) {
    return { chapterId: chapter.id, ttsId };
  }
  const firstChapter = documentSummary.chapters[0];
  const selectedChapter = chapter ?? firstChapter;
  if (!selectedChapter) return;
  return findFirstAudioChunkAtOrAfterChapter(
    documentSummary,
    selectedChapter.id,
  );
}

export function didLegacyAudioChunkSelectionFallback(
  selectedAudioChunk: { chapterId: string; ttsId: string },
  requestedChapterId: string | undefined,
  requestedTtsId: string | undefined,
) {
  return (
    (requestedChapterId !== undefined &&
      selectedAudioChunk.chapterId !== requestedChapterId) ||
    selectedAudioChunk.ttsId !== requestedTtsId
  );
}

export function isLegacyAudioChunkSelectionStale(
  documentSummary: Pick<AudioItemDetail, 'chapters'>,
  chapterId: string | undefined,
  ttsId: string | undefined,
) {
  if (!chapterId && !ttsId) return false;
  const chapter = chapterId
    ? documentSummary.chapters.find((c) => c.id === chapterId)
    : undefined;
  if (!chapter) return true;
  return ttsId !== undefined && !chapter.tts_ids.includes(ttsId);
}

/** The chunk after `currentTtsId`, crossing into the next chapter when this one
 * runs out. `undefined` at the end of the document — and, as above, when the
 * ids no longer describe this summary. */
export function findNextAudioChunk(
  documentSummary: Pick<AudioItemDetail, 'chapters'>,
  currentChapterId: string,
  currentTtsId: string,
) {
  const chapters = documentSummary.chapters;
  const chapterIdx = chapters.findIndex(
    (chapter) => chapter.id === currentChapterId,
  );
  const chapter = chapters[chapterIdx];
  if (!chapter) return;
  const ttsIdxInCurrentChapter = chapter.tts_ids.indexOf(currentTtsId);
  if (ttsIdxInCurrentChapter === -1) return;
  const nextTtsIdInChapter = chapter.tts_ids[ttsIdxInCurrentChapter + 1];
  if (nextTtsIdInChapter) {
    return { chapterId: currentChapterId, ttsId: nextTtsIdInChapter };
  }
  const nextChapter = chapters[chapterIdx + 1];
  if (!nextChapter) return;
  return findFirstAudioChunkAtOrAfterChapter(documentSummary, nextChapter.id);
}

export function decodedPrefetchWarmCount({
  poolSize,
  prefetchCount,
}: {
  poolSize: number;
  prefetchCount: number;
}) {
  if (poolSize <= 1 || prefetchCount <= 0) return 0;
  return Math.min(prefetchCount, Math.max(1, Math.floor(poolSize) - 1));
}
